import { ParsedListing } from '@bb/domain/dist/types';
import React, {
  createContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Text, message } from 'components';
import { makeModalResponsive } from 'utils';
import { useDebounce } from 'hooks';
import errorReporter from '@app/errorReporter';
import type { UnregisterCallback } from 'history';
import { toggleUseAppointment } from 'api/AppointmentDeskController';
import {
  getListingsForAppointmentDesk,
  getEnabledListingCountForAppointmentDesk,
} from 'api/ListingController';
import { AntdModal } from '@brokerbay/design-components';
import { ModalType, ModalMapType } from './types';
import AnsweringServiceContext, {
  AnsweringServiceSubscribeStep,
} from '../AnsweringServiceContext';
import FormContext from '../../UserViewPage/FormContext';

type QueryParameterType = {
  searchTerm: string;
  filter: {
    location?: string[];
    availability?: string[];
    useAppointmentDesk?: boolean[];
  };
  pageNumber: number;
  sortData: {
    field: string;
    order: string;
  };
};

type SearchResultType = {
  count: number;
  listings: ParsedListing[];
};

type ManageListingsContextType = {
  // states
  searchResults: SearchResultType;
  loading: boolean;
  queryParameter: QueryParameterType;
  totalCount: number;
  enabledCount: number;
  // update states
  setSearchResults: (searchResults: SearchResultType) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setQueryParameter: React.Dispatch<React.SetStateAction<QueryParameterType>>;
  setEnabledListingsCount: React.Dispatch<React.SetStateAction<number>>;
  // refs
  containerRef: React.MutableRefObject<HTMLDivElement>;
  footerRef: React.MutableRefObject<HTMLDivElement>;
  // functions
  openModal: (type: ModalType) => void;
  sort: (field: string) => void;
};

const initialQueryData: any = {
  searchTerm: '',
  filter: {},
  pageNumber: 0,
  sortData: {
    field: '',
    order: undefined,
  },
};

const ManageListingsContext = createContext<ManageListingsContextType>(null);

export const ManageListingsContextProvider: React.FC = ({ children }) => {
  // Set up some states to track values
  const [searchResults, setSearchResults] = React.useState<SearchResultType>({
    count: 0,
    listings: [],
  });
  const [loading, setLoading] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [queryParameter, setQueryParameter] =
    React.useState<QueryParameterType>(initialQueryData);
  const [totalCount, setTotalCount] = React.useState(0);
  const [enabledCount, setEnabledListingsCount] = React.useState(0);

  // Set up some refs to track values
  const containerRef = useRef<HTMLDivElement>();
  const footerRef = useRef<HTMLDivElement>();
  /** The function to unblock the user from navigating away from the page. */
  const unBlock = useRef<UnregisterCallback>();
  /** The URL that the user wants to navigate to if they try to leave the ManageListings page. */
  const pathName = useRef<string>();
  const firstRun = useRef(true);

  // Grab some values from contexts
  const { showWarningRef } = useContext(FormContext);
  const { currentStep, hasActiveAnsweringService, userId, viewingLevel } =
    useContext(AnsweringServiceContext);

  const debouncedSearchTerm = useDebounce(queryParameter.searchTerm, 500);
  const history = useHistory();

  const sort = (field: string) => {
    setQueryParameter(prev => {
      const { sortData } = prev;
      const order =
        field === sortData.field
          ? {
              ascend: 'descend',
              descend: null,
              null: 'ascend',
            }[sortData.order]
          : 'ascend';
      return {
        ...prev,
        sortData: { field: order ? field : '', order },
      };
    });
  };

  const runSearch = useCallback(async () => {
    try {
      setLoading(true);

      const getFormattedSortData = () => {
        const { field, order } = queryParameter.sortData;
        const sort_field = field.toString();

        return {
          sort_field,
          sort_asc: (order === 'ascend').toString() as 'true' | 'false',
        };
      };

      const newSearchParameter = {
        ...getFormattedSortData(),
        subscriptionType: viewingLevel,
        userId,
        address: queryParameter.searchTerm,
        pageNumber: queryParameter.pageNumber,
        locations: queryParameter.filter.location?.map(location =>
          location.toString(),
        ),
        availability: queryParameter.filter.availability,
        useAppointmentDesk: queryParameter.filter.useAppointmentDesk,
      };

      let listings: ParsedListing[];
      let count: number;
      if (firstRun.current) {
        const [searchResult, enabledListingCount] = await Promise.all([
          getListingsForAppointmentDesk(newSearchParameter),
          getEnabledListingCountForAppointmentDesk(viewingLevel),
        ]);
        ({ count, listings } = searchResult);
        firstRun.current = false;
        setTotalCount(count);
        setEnabledListingsCount(enabledListingCount.count);
      } else {
        ({ count, listings } =
          await getListingsForAppointmentDesk(newSearchParameter));
      }

      setSearchResults(prevResults => ({
        ...prevResults,
        listings,
        count,
      }));
    } catch (error) {
      message.error('Error searching listings');
      errorReporter.error('Error searching listing', error);
    } finally {
      setLoading(false);
    }
  }, [queryParameter, viewingLevel, userId]);

  useEffect(() => {
    runSearch();
  }, [
    debouncedSearchTerm,
    queryParameter.pageNumber,
    queryParameter.filter,
    queryParameter.sortData,
    runSearch,
  ]);

  const toggleUseAppointmentForListings = useCallback(
    async (toggleOn: boolean) => {
      try {
        setLoading(true);
        const body = {
          subscriptionType: viewingLevel,
          toggleOn,
        };
        await toggleUseAppointment(body);

        if (toggleOn) setEnabledListingsCount(totalCount);
        else setEnabledListingsCount(0);

        setSearchResults(prev => ({
          ...prev,
          listings: prev.listings.map(listing => ({
            ...listing,
            useAppointmentDesk: toggleOn,
          })),
        }));
      } catch (error) {
        message.error(`Error ${toggleOn ? 'enabling' : 'disabling'} listings`);
        errorReporter.error(
          `Error ${toggleOn ? 'enabling' : 'disabling'} listings`,
          error,
        );
      } finally {
        setLoading(false);
      }
    },
    [totalCount, viewingLevel],
  );

  const modalTypeMap: ModalMapType = useMemo(
    () => ({
      leaveThePage: {
        title: (
          <span data-cy="leave-page-modal-title">
            Are you sure you want to leave?
          </span>
        ),
        content: (
          <span data-cy="leave-page-modal-text">
            Your live concierge service has not been activated. Are you sure you
            want to leave this page?
          </span>
        ),
        onOk: () => {
          unBlock.current();
          history.replace(pathName.current);
        },

        onCancel: () => {
          setIsModalOpen(false);
          history.replace(history.location.pathname);
        },
      },
      enableListings: {
        title:
          'Are you sure you want to enable all listings to the Live Concierge Service?',
        onOk: () => {
          toggleUseAppointmentForListings(true);
        },
      },
      disableListings: {
        title:
          'Are you sure you want to disable all listings from the Live Concierge Service?',
        onOk: () => {
          toggleUseAppointmentForListings(false);
        },
      },
    }),
    [history, toggleUseAppointmentForListings],
  );

  /**
   * Handle the opening and closing of a modal during the subscription flow. Used on multiple pages with different content.
   * */
  const openModal = useCallback(
    (type: ModalType) => {
      const { title, content, onOk, onCancel } = modalTypeMap[type];
      setIsModalOpen(true);
      makeModalResponsive(
        // Note: we are using AntdModal directly here because we have a functionality in
        // common Modal component that whenever the route changes, it closes all open modals
        // which in this case causing a flickering issue.
        AntdModal.confirm({
          title,
          okText: 'Yes',
          cancelText: 'Cancel',
          onOk,
          centered: true,
          ...(onCancel && { onCancel }),
          ...(content && {
            content: <Text type="secondary">{content}</Text>,
          }),
        }),
        {
          dataCys: {
            ok: `${title}-modal-confirm`,
            cancel: `${title}-modal-cancel`,
            content: `${title}-modal-content`,
          },
        },
      );
    },
    [modalTypeMap],
  );

  useEffect(() => {
    // Apply the following only to the Manage Listings page.
    if (currentStep === AnsweringServiceSubscribeStep.SubscriptionSuccess) {
      return;
    }

    if (!hasActiveAnsweringService) {
      if (showWarningRef)
        // Ensure the FormContext modal doesn't interfere with ours
        showWarningRef.current = true;

      // Any time the user tries to navigate away from this page,
      // block it and trigger this code to open the modal instead.
      // (history.block will be the return the function to unblock.)
      unBlock.current = history.block(({ pathname }) => {
        if (pathname === history.location.pathname) {
          return;
        }
        // Set our variable to wherever the user was trying to go
        pathName.current = pathname;
        // If modal is already opened then avoid opening multiple modals
        if (!isModalOpen) {
          openModal('leaveThePage');
        }
        return false;
      });

      // Trigger a default browser warning if the user tries to refresh/close the tab
      const preventRefresh = (event: BeforeUnloadEvent) => {
        event.preventDefault();
      };
      window.addEventListener('beforeunload', preventRefresh);

      // Remove our listners to trigger the modal. Give FormContext control back (if on a page with that context).
      return () => {
        unBlock.current();
        window.removeEventListener('beforeunload', preventRefresh);
        if (showWarningRef) showWarningRef.current = false;
      };
    }
  }, [
    currentStep,
    hasActiveAnsweringService,
    history,
    isModalOpen,
    openModal,
    showWarningRef,
  ]);

  return (
    <ManageListingsContext.Provider
      value={{
        // states
        searchResults,
        loading,
        queryParameter,
        totalCount,
        enabledCount,
        // update states
        setSearchResults,
        setLoading,
        setQueryParameter,
        setEnabledListingsCount,
        // refs
        containerRef,
        footerRef,
        // functions
        openModal,
        sort,
      }}
    >
      {children}
    </ManageListingsContext.Provider>
  );
};

export default ManageListingsContext;
